import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  IconButton,
  Button,
  Typography,
  Grid,
  Slide,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Paper,
  Chip,
  Stack,
  Backdrop,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle,
  Rocket,
  Error as ErrorIcon,
  ContentCopy,
  CalendarToday,
  Phone,
  Email,
  Person,
  Business,
  AccessTime,
  VerifiedUser,
  Support,
  Schedule,
  Diamond,
  Star,
  LocalOffer,
  CloudUpload,
  Send,
} from "@mui/icons-material";

// Import the API function
import { createNewLead } from "../../Action/crmApi";
import { PRODUCT_NAME } from "../../Config";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Loading Component
const LoadingPopup = ({ open, message }) => (
  <Backdrop
    sx={{
      color: '#fff',
      zIndex: (theme) => theme.zIndex.drawer + 1,
      backdropFilter: 'blur(8px)',
      background: 'rgba(0, 0, 0, 0.7)'
    }}
    open={open}
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(145deg, #667eea, #764ba2)',
        borderRadius: 4,
        p: 6,
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        border: '2px solid rgba(255, 255, 255, 0.1)',
        minWidth: 300,
        textAlign: 'center'
      }}
    >
      {/* Animated Loading Icon */}
      <Box
        sx={{
          position: 'relative',
          mb: 3,
          animation: 'loadingPulse 2s ease-in-out infinite',
          '@keyframes loadingPulse': {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.1)' },
            '100%': { transform: 'scale(1)' }
          }
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -2,
              left: -2,
              right: -2,
              bottom: -2,
              borderRadius: '50%',
              background: 'conic-gradient(from 0deg, transparent, #fff, transparent)',
              animation: 'rotate 2s linear infinite',
            },
            '@keyframes rotate': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        >
          <Send sx={{ fontSize: 40, color: 'white' }} />
        </Box>
      </Box>

      {/* Loading Text */}
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: 'white' }}>
        Processing Your Request
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, color: 'white' }}>
        {message}
      </Typography>

      {/* Progress Dots */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'white',
              opacity: 0.3,
              animation: `dotPulse 1.5s ease-in-out infinite ${index * 0.3}s`,
              '@keyframes dotPulse': {
                '0%, 80%, 100%': { opacity: 0.3 },
                '40%': { opacity: 1 }
              }
            }}
          />
        ))}
      </Box>

      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
        Please wait, do not close this window...
      </Typography>
    </Box>
  </Backdrop>
);

const CustomerRequestForm = ({ open, onClose, requestType = "GetStarted", selectedPlan = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    bestTime: '',
    preferredContact: '',
    employees: '',
    country: '',
    reason: '',
    planType: selectedPlan || '' // Pre-fill with selected plan
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [apiResponse, setApiResponse] = useState(null);
  const [copiedRef, setCopiedRef] = useState(false);

  // Update form data when selectedPlan changes
  useEffect(() => {
    if (selectedPlan && open) {
      setFormData(prev => ({
        ...prev,
        planType: selectedPlan
      }));
    }
  }, [selectedPlan, open]);

  const handleClose = () => {
    // Prevent closing during submission
    if (isSubmitting) {
      return;
    }

    setFormData({
      name: '',
      phone: '',
      email: '',
      bestTime: '',
      preferredContact: '',
      employees: '',
      country: '',
      reason: '',
      planType: selectedPlan || '' // Reset to selected plan or empty
    });
    setFormErrors({});
    setIsSubmitting(false);
    setSubmitStatus(null);
    setApiResponse(null);
    setCopiedRef(false);
    onClose();
  };

  const handleFormChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    // Clear any previous submit status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
  };

  const validateForm = () => {
    const errors = {};
    const requiredFields = ['name', 'phone', 'email', 'bestTime', 'preferredContact', 'country', 'reason', 'planType'];
    
    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        errors[field] = 'This field is required';
      }
    });

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const copyReferenceNumber = async () => {
    if (apiResponse?.data?.leadId) {
      try {
        await navigator.clipboard.writeText(apiResponse.data.leadId);
        setCopiedRef(true);
        setTimeout(() => setCopiedRef(false), 2000);
      } catch (err) {
        console.error('Failed to copy reference number:', err);
      }
    }
  };

  const getLoadingMessage = () => {
    switch(requestType) {
      case 'Start Free Trial':
        return 'Setting up your free trial account...';
      case 'WatchDemo':
        return 'Scheduling your personalized demo...';
      case 'GetStarted':
      default:
        return 'Submitting your request to our team...';
    }
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Prepare the data for API call
      const leadData = {
        fullName: formData.name,
        phoneNumber: formData.phone,
        email: formData.email,
        bestTimeToConnect: formData.bestTime,
        preferredContactMethod: formData.preferredContact,
        numberOfEmployees: formData.employees || null,
        country: formData.country,
        reasonForRequest: formData.reason,
        planType: formData.planType,
        requestType: requestType, // Added requestType from props
        // Additional metadata
        submittedAt: new Date().toISOString(),
        source: `{${PRODUCT_NAME}} Website`
      };

      console.log('Submitting lead data:', leadData);

      // Call the API
      const result = await createNewLead(leadData);

      if (result.success) {
        console.log('Lead created successfully:', result.data);
        setApiResponse(result.data); // Store the full API response
        setSubmitStatus('success');
      } else {
        console.error('Failed to create lead:', result.error);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubmitButtonContent = () => {
    if (isSubmitting) {
      return (
        <>
          <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
          Processing...
        </>
      );
    }
    if (submitStatus === 'success') {
      return (
        <>
          <CheckCircle sx={{ mr: 1 }} />
          Success!
        </>
      );
    }
    return (
      <>
        <Rocket sx={{ mr: 1 }} />
        Submit Request
      </>
    );
  };

  const getFormTitle = () => {
    switch(requestType) {
      case 'Start Free Trial':
        return `🚀 Start Your Free Trial with ${PRODUCT_NAME}`;
      case 'WatchDemo':
        return `🎬 Watch ${PRODUCT_NAME} Demo`;
      case 'GetStarted':
      default:
        return `🚀 Get Started with ${PRODUCT_NAME}`;
    }
  };

  const getFormSubtitle = () => {
    switch(requestType) {
      case 'Start Free Trial':
        return 'Begin your 14-day free trial - No credit card required!';
      case 'WatchDemo':
        return 'Schedule a personalized demo with our experts';
      case 'GetStarted':
      default:
        return 'Fill out the form below and we\'ll get in touch within 24 hours';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // Return original if formatting fails
    }
  };

  const formatTime = (timeValue) => {
    const timeMap = {
      'morning': 'Morning (9 AM - 12 PM)',
      'afternoon': 'Afternoon (12 PM - 6 PM)',
      'evening': 'Evening (6 PM - 9 PM)'
    };
    return timeMap[timeValue] || timeValue;
  };

  const formatCountry = (countryCode) => {
    const countryMap = {
      'us': 'United States',
      'uk': 'United Kingdom',
      'ca': 'Canada',
      'au': 'Australia',
      'in': 'India',
      'de': 'Germany',
      'fr': 'France',
      'jp': 'Japan',
      'br': 'Brazil',
      'other': 'Other'
    };
    return countryMap[countryCode] || countryCode;
  };

  const formatPlanType = (planValue) => {
    const planMap = {
      'starter': 'Starter Plan',
      'professional': 'Professional Plan', 
      'enterprise': 'Enterprise Plan'
    };
    return planMap[planValue] || planValue;
  };

  const getPlanIcon = (planValue) => {
    switch(planValue) {
      case 'starter': return <LocalOffer sx={{ color: '#10b981' }} />;
      case 'professional': return <Star sx={{ color: '#f59e0b' }} />;
      case 'enterprise': return <Diamond sx={{ color: '#8b5cf6' }} />;
      default: return <LocalOffer sx={{ color: '#667eea' }} />;
    }
  };

  const getRequestTypeMessage = () => {
    switch(requestType) {
      case 'Start Free Trial':
        return 'Your free trial request has been processed successfully!';
      case 'WatchDemo':
        return 'Your demo request has been scheduled successfully!';
      case 'GetStarted':
      default:
        return 'Your request has been received and processed successfully!';
    }
  };

  // Success Screen Component
  const SuccessScreen = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      {/* Success Animation */}
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10b981, #059669)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 3,
          position: 'relative',
          animation: 'successPulse 2s infinite',
          '@keyframes successPulse': {
            '0%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.4)' },
            '50%': { transform: 'scale(1.05)', boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)' },
            '100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)' }
          }
        }}
      >
        <CheckCircle sx={{ fontSize: 60, color: 'white' }} />
      </Box>

      {/* Success Message */}
      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#10b981', mb: 2 }}>
        🎉 Request Submitted Successfully!
      </Typography>
      
      <Typography variant="h6" sx={{ mb: 4, color: '#374151', maxWidth: 500, mx: 'auto' }}>
        Thank you for choosing <strong>{PRODUCT_NAME}</strong>! {getRequestTypeMessage()}
      </Typography>

      {/* Reference Information Card */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 4,
          background: 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
          border: '2px solid #10b981',
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: 'linear-gradient(90deg, #10b981, #059669, #10b981)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <VerifiedUser sx={{ color: '#10b981', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151' }}>
            Your Reference Details
          </Typography>
        </Box>

        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={3}>
            <Box
              sx={{
                background: 'white',
                p: 2,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                Reference Number
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#667eea' }}>
                  #{apiResponse?.data?.leadId}
                </Typography>
                <IconButton
                  onClick={copyReferenceNumber}
                  size="small"
                  sx={{
                    color: copiedRef ? '#10b981' : '#6b7280',
                    '&:hover': { background: 'rgba(102, 126, 234, 0.1)' }
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              {copiedRef && (
                <Typography variant="caption" sx={{ color: '#10b981' }}>
                  Copied!
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={3}>
            <Box
              sx={{
                background: 'white',
                p: 2,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                Submission Date
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <CalendarToday sx={{ fontSize: 20, color: '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#374151' }}>
                  {formatDate(apiResponse?.data?.createdDate)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box
              sx={{
                background: 'white',
                p: 2,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                Selected Plan
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                {getPlanIcon(formData.planType)}
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#374151' }}>
                  {formData.planType.charAt(0).toUpperCase() + formData.planType.slice(1)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Box
              sx={{
                background: 'white',
                p: 2,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}
            >
              <Typography variant="body2" sx={{ color: '#6b7280', mb: 1 }}>
                Request Type
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Rocket sx={{ fontSize: 20, color: '#667eea' }} />
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#374151' }}>
                  {requestType === 'Start Free Trial' ? 'Free Trial' : 
                   requestType === 'WatchDemo' ? 'Demo' : 
                   'Get Started'}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Alert
          severity="info"
          sx={{
            mt: 3,
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            '& .MuiAlert-icon': { color: '#3b82f6' }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            📋 Please save your reference number for future communication
          </Typography>
        </Alert>
      </Paper>

      {/* What Happens Next */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 4,
          background: 'white',
          borderRadius: 3,
          textAlign: 'left'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3, textAlign: 'center', color: '#374151' }}>
          🚀 What Happens Next?
        </Typography>
        
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Schedule sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#374151' }}>
                Within 2-4 Hours
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                Our customer success team will review your {formatPlanType(formData.planType)} {requestType.toLowerCase()} request and prepare a personalized consultation plan.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Phone sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#374151' }}>
                Personal Contact - {formatTime(formData.bestTime)}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                We'll reach out via {formData.preferredContact} during your preferred time to discuss your {PRODUCT_NAME} journey and {formatPlanType(formData.planType)} features.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <Business sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#374151' }}>
                {requestType === 'Start Free Trial' ? 'Account Setup' : 
                 requestType === 'WatchDemo' ? 'Custom Demo' : 
                 'Custom Demo & Pricing'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280' }}>
                {requestType === 'Start Free Trial' ? 
                 `Get your free trial account activated and start using ${PRODUCT_NAME} immediately.` :
                 requestType === 'WatchDemo' ? 
                 `Experience a live demonstration of ${PRODUCT_NAME} features tailored to your needs.` :
                 `Get a tailored demonstration of ${PRODUCT_NAME} features for the ${formatPlanType(formData.planType)} and receive personalized pricing.`}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Paper>

      {/* Contact Information */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          color: 'white',
          borderRadius: 3,
          mb: 3
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          📧 Need Immediate Assistance?
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Email sx={{ fontSize: 20 }} />
              <Typography variant="body2">support@oruclicks.com</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Phone sx={{ fontSize: 20 }} />
              <Typography variant="body2">+1 (555) 123-4567</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary of Submitted Information */}
      <Box sx={{ textAlign: 'left', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, color: '#374151' }}>
          📋 Your Submitted Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Name: <strong>{formData.name}</strong></Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Email: <strong>{formData.email}</strong></Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Phone: <strong>{formData.phone}</strong></Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Plan: <strong>{formatPlanType(formData.planType)}</strong></Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Best Time: <strong>{formatTime(formData.bestTime)}</strong></Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Contact Method: <strong>{formData.preferredContact}</strong></Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Country: <strong>{formatCountry(formData.country)}</strong></Typography>
            <Typography variant="body2" sx={{ color: '#6b7280' }}>Request Type: <strong>{requestType}</strong></Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Loading Popup */}
      <LoadingPopup 
        open={isSubmitting} 
        message={getLoadingMessage()} 
      />

      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
        maxWidth={submitStatus === 'success' ? "lg" : "md"}
        fullWidth
        disableEscapeKeyDown={isSubmitting} // Prevent ESC key during submission
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: "linear-gradient(145deg, #f8fafc, #ffffff)",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            maxHeight: '90vh'
          }
        }}
      >
        {submitStatus !== 'success' && (
          <DialogTitle sx={{ 
            textAlign: "center", 
            pb: 2,
            pt: 2,
            background: "linear-gradient(135deg, #667eea, #764ba2)",
            color: "white",
            position: "relative"
          }}>
            <IconButton
              onClick={handleClose}
              disabled={isSubmitting} // Disable close button during submission
              sx={{ 
                position: "absolute", 
                right: 8, 
                top: 8,
                color: "white",
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: "bold", mt: 1 }}>
              {getFormTitle()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
              {getFormSubtitle()}
            </Typography>
          </DialogTitle>
        )}
        
        <DialogContent sx={{ 
          p: submitStatus === 'success' ? 2 : 4, 
          pt: submitStatus === 'success' ? 2 : 3,
          "&.MuiDialogContent-root": {
            paddingTop: submitStatus === 'success' ? "16px !important" : "24px !important"
          },
          maxHeight: '70vh',
          overflowY: 'auto'
        }}>
          {submitStatus === 'success' ? (
            <SuccessScreen />
          ) : (
            <>
              {/* Error Messages */}
              {submitStatus === 'error' && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 3 }}
                  icon={<ErrorIcon />}
                >
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    Submission Failed
                  </Typography>
                  <Typography variant="body2">
                    There was an error submitting your request. Please try again or contact support.
                  </Typography>
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name *"
                    value={formData.name}
                    onChange={handleFormChange('name')}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    disabled={isSubmitting}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    value={formData.phone}
                    onChange={handleFormChange('phone')}
                    error={!!formErrors.phone}
                    helperText={formErrors.phone}
                    disabled={isSubmitting}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Email Address *"
                    type="email"
                    value={formData.email}
                    onChange={handleFormChange('email')}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    disabled={isSubmitting}
                    sx={{ mb: 2 }}
                  />
                  <FormControl fullWidth error={!!formErrors.bestTime} sx={{ mb: 2 }}>
                    <InputLabel>Best Time to Connect *</InputLabel>
                    <Select
                      value={formData.bestTime}
                      onChange={handleFormChange('bestTime')}
                      label="Best Time to Connect *"
                      disabled={isSubmitting}
                    >
                      <MenuItem value="morning">Morning (9 AM - 12 PM)</MenuItem>
                      <MenuItem value="afternoon">Afternoon (12 PM - 6 PM)</MenuItem>
                      <MenuItem value="evening">Evening (6 PM - 9 PM)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl component="fieldset" error={!!formErrors.preferredContact} sx={{ mb: 2 }}>
                    <FormLabel component="legend" sx={{ fontWeight: "bold", mb: 1 }}>
                      Preferred Contact Method *
                    </FormLabel>
                    <RadioGroup
                      value={formData.preferredContact}
                      onChange={handleFormChange('preferredContact')}
                      row
                    >
                      <FormControlLabel 
                        value="email" 
                        control={<Radio />} 
                        label="Email" 
                        disabled={isSubmitting}
                      />
                      <FormControlLabel 
                        value="phone" 
                        control={<Radio />} 
                        label="Phone" 
                        disabled={isSubmitting}
                      />
                    </RadioGroup>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Number of Employees (Optional)"
                    value={formData.employees}
                    onChange={handleFormChange('employees')}
                    disabled={isSubmitting}
                    sx={{ mb: 2 }}
                  />
                  
                  <FormControl fullWidth error={!!formErrors.country} sx={{ mb: 2 }}>
                    <InputLabel>Country *</InputLabel>
                    <Select
                      value={formData.country}
                      onChange={handleFormChange('country')}
                      label="Country *"
                      disabled={isSubmitting}
                    >
                      <MenuItem value="us">United States</MenuItem>
                      <MenuItem value="uk">United Kingdom</MenuItem>
                      <MenuItem value="ca">Canada</MenuItem>
                      <MenuItem value="au">Australia</MenuItem>
                      <MenuItem value="in">India</MenuItem>
                      <MenuItem value="de">Germany</MenuItem>
                      <MenuItem value="fr">France</MenuItem>
                      <MenuItem value="jp">Japan</MenuItem>
                      <MenuItem value="br">Brazil</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControl fullWidth error={!!formErrors.planType} sx={{ mb: 2 }}>
                    <InputLabel>Select Plan *</InputLabel>
                    <Select
                      value={formData.planType}
                      onChange={handleFormChange('planType')}
                      label="Select Plan *"
                      disabled={isSubmitting}
                    >
                      <MenuItem value="starter">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocalOffer sx={{ color: '#10b981', fontSize: 20 }} />
                          Starter Plan
                        </Box>
                      </MenuItem>
                      <MenuItem value="professional">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Star sx={{ color: '#f59e0b', fontSize: 20 }} />
                          Professional Plan (Most Popular)
                        </Box>
                      </MenuItem>
                      <MenuItem value="enterprise">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Diamond sx={{ color: '#8b5cf6', fontSize: 20 }} />
                          Enterprise Plan
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Reason for Request *"
                    value={formData.reason}
                    onChange={handleFormChange('reason')}
                    error={!!formErrors.reason}
                    helperText={formErrors.reason || "Please tell us about your marketing needs"}
                    placeholder="e.g., Looking to automate social media marketing for my business..."
                    disabled={isSubmitting}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: 4, 
          pt: 2,
          justifyContent: "center",
          gap: 2
        }}>
          {submitStatus === 'success' ? (
            <Button
              onClick={handleClose}
              variant="contained"
              size="large"
              sx={{
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                fontWeight: "bold",
                px: 6,
                py: 1.5,
                "&:hover": {
                  background: "linear-gradient(135deg, #5a67d8, #6b46c1)"
                }
              }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={handleClose}
                variant="outlined"
                disabled={isSubmitting}
                sx={{
                  borderColor: "#667eea",
                  color: "#667eea",
                  px: 4,
                  py: 1.5,
                  fontWeight: "bold",
                  opacity: isSubmitting ? 0.5 : 1,
                  "&:hover": {
                    borderColor: "#5a67d8",
                    background: "rgba(102,126,234,0.1)"
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleFormSubmit}
                variant="contained"
                disabled={isSubmitting}
                sx={{
                  background: "linear-gradient(135deg, #667eea, #764ba2)",
                  fontWeight: "bold",
                  px: 4,
                  py: 1.5,
                  minWidth: 160,
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a67d8, #6b46c1)"
                  }
                }}
              >
                {getSubmitButtonContent()}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomerRequestForm;